/**
 * Client-side draft store (IndexedDB via idb). Persists the resumable
 * IngestDraft on-device so a refresh or the M7 save/signup interstitial doesn't
 * lose the parsed + mapped dataset (criterion 11). Browser-only; guarded so it
 * no-ops (returns null/empty) under SSR or where IndexedDB is unavailable.
 */

import { openDB, type IDBPDatabase } from "idb";
import type { IngestDraft } from "./types";

const DB_NAME = "plotzio-ingest";
const STORE = "drafts";
const DB_VERSION = 1;

function available(): boolean {
  return typeof indexedDB !== "undefined";
}

let dbPromise: Promise<IDBPDatabase> | null = null;
function db(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE)) {
          database.createObjectStore(STORE, { keyPath: "draft_id" });
        }
      },
    });
  }
  return dbPromise;
}

export async function saveDraft(draft: IngestDraft): Promise<void> {
  if (!available()) return;
  await (await db()).put(STORE, draft);
}

export async function loadDraft(draftId: string): Promise<IngestDraft | null> {
  if (!available()) return null;
  return (await (await db()).get(STORE, draftId)) ?? null;
}

export async function listDrafts(): Promise<IngestDraft[]> {
  if (!available()) return [];
  return (await (await db()).getAll(STORE)) as IngestDraft[];
}

export async function deleteDraft(draftId: string): Promise<void> {
  if (!available()) return;
  await (await db()).delete(STORE, draftId);
}
